import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../../context/AdminAuthContext';

const RatingsManagement = () => {
  const { adminToken } = useAdminAuth();
  const queryClient = useQueryClient();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [ratingToDelete, setRatingToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

   const fetchRatings = async ({ pageParam = 1 }) => {
    try {
      const response = await fetch(`https://question-manager-backend.onrender.com/api/rating/admin/all-ratings?page=${pageParam}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب التعليقات');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب التعليقات: ' + error.message);
      throw error;
    }
  };

   const { data: ratingsData, isLoading: ratingsLoading, error: ratingsError } = useQuery({
    queryKey: ['adminRatings', currentPage],
    queryFn: () => fetchRatings({ pageParam: currentPage }),
  });

   const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

   const deleteRatingMutation = useMutation({
    mutationFn: async (ratingId) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/rating/admin/${ratingId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في حذف التعليق');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في حذف التعليق: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم حذف التعليق بنجاح');
      queryClient.invalidateQueries(['adminRatings']);
      setShowDeleteConfirmation(false);
      setRatingToDelete(null);
    },
  });

   const updateRatingVisibilityMutation = useMutation({
    mutationFn: async ({ ratingId, isVisible }) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/rating/admin/${ratingId}/visibility`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isVisible }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في تحديث حالة التعليق');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في تحديث حالة التعليق: ' + error.message);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      const action = variables.isVisible ? 'إظهار' : 'إخفاء';
      showNotification('success', `تم ${action} التعليق بنجاح`);
      queryClient.invalidateQueries(['adminRatings']);
    },
  });

   const Notification = ({ notification }) => {
    if (!notification) return null;
    
    const bgColor = notification.type === 'success' ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white shadow-lg z-50 ${bgColor} animate-fade-in-out`}>
        <div className="flex items-center">
          {notification.type === 'success' ? (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          ) : (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          )}
          <span>{notification.message}</span>
        </div>
      </div>
    );
  };

   const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="flex gap-2">
        <div className="h-8 bg-gray-200 rounded flex-1"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  );

   const DeleteConfirmation = ({ rating, onCancel, onConfirm }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="text-center mb-6">
            <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">تأكيد حذف التعليق</h2>
            <p className="text-gray-600">
              هل أنت متأكد أنك تريد حذف هذا التعليق؟ 
              <br />
              هذا الإجراء لا يمكن التراجع عنه.
            </p>
          </div>
          
          <div className="flex gap-2 justify-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              نعم، احذف التعليق
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteRating = () => {
    if (ratingToDelete) {
      deleteRatingMutation.mutate(ratingToDelete._id);
    }
  };

  const handleToggleVisibility = (rating) => {
    updateRatingVisibilityMutation.mutate({
      ratingId: rating._id,
      isVisible: !rating.isVisible
    });
  };

   const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

   const Pagination = ({ pagination }) => {
    if (!pagination) return null;

    const { currentPage, totalPages, hasNextPage, hasPrevPage } = pagination;

    return (
      <div className="flex justify-center items-center space-x-2 mt-6">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={!hasPrevPage}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          السابق
        </button>
        
        <span className="px-3 py-2 text-sm text-gray-700">
          الصفحة {currentPage} من {totalPages}
        </span>
        
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={!hasNextPage}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          التالي
        </button>
      </div>
    );
  };

  if (ratingsLoading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <SkeletonCard key={item} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (ratingsError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">حدث خطأ</h2>
          <p className="text-gray-600 mb-6">{ratingsError.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

   const { ratings, pagination, statistics } = ratingsData || { 
    ratings: [], 
    pagination: {},
    statistics: {
      totalRatings: 0,
      visibleRatings: 0,
      hiddenRatings: 0
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
       <Notification notification={notification} />
      
       <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">إدارة التعليقات والتقييمات</h1>
              {/* <p className="text-gray-600 text-sm">
                يمكنك إدارة تعليقات المستخدمين من هنا
              </p> */}
            </div>
          </div>
        </div>
      </header>

 <div className="bg-white">
  <div className="max-w-6xl mx-auto px-4 py-6">

     <div className="grid grid-cols-3 gap-3">

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">إجمالي التعليقات</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{statistics.totalRatings}</span>
          <span className="text-xs ml-2 mb-1">تعليق</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">التعليقات الظاهرة</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{statistics.visibleRatings}</span>
          <span className="text-xs ml-2 mb-1">تعليق</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">التعليقات المخفية</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{statistics.hiddenRatings}</span>
          <span className="text-xs ml-2 mb-1">تعليق</span>
        </div>
      </div>

    </div>

  </div>
</div>

       <div className="max-w-6xl mx-auto px-4 py-6">
         {/* <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                type="text"
                placeholder="ابحث في التعليقات..."
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="w-full md:w-48">
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">جميع الحالات</option>
                <option value="visible">الظاهرة فقط</option>
                <option value="hidden">المخفية فقط</option>
              </select>
            </div>
            <div className="w-full md:w-48">
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">الترتيب بالأحدث</option>
                <option value="oldest">الترتيب بالأقدم</option>
              </select>
            </div>
          </div>
        </div> */}

        {ratings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-indigo-100">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">لا توجد تعليقات حالياً</h3>
            <p className="text-gray-600 mb-6">لم يتم إضافة أي تعليقات بعد</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {ratings.map((rating) => (
                <div key={rating._id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-md">
                  <div className={`h-2 ${rating.isVisible ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{rating.username}</h3>
                          <p className="text-xs text-gray-500">{formatDate(rating.createdAt)}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        rating.isVisible ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {rating.isVisible ? 'ظاهر' : 'مخفي'}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {rating.description}
                    </p>
                    
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleToggleVisibility(rating)}
                        className={`flex-1 px-2 py-2 rounded-md text-sm transition-colors flex items-center justify-center ${
                          rating.isVisible 
                            ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' 
                            : 'text-green-700 bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {rating.isVisible ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          )}
                        </svg>
                        {rating.isVisible ? 'إخفاء' : 'إظهار'}
                      </button>
                      <button
                        onClick={() => {
                          setRatingToDelete(rating);
                          setShowDeleteConfirmation(true);
                        }}
                        className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

             <Pagination pagination={pagination} />
          </>
        )}
      </div>

       {showDeleteConfirmation && ratingToDelete && (
        <DeleteConfirmation 
          rating={ratingToDelete} 
          onCancel={() => {
            setShowDeleteConfirmation(false);
            setRatingToDelete(null);
          }}
          onConfirm={handleDeleteRating}
        />
      )}

       <footer className="bg-white border-t border-gray-200 mt-8 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
         </div>
      </footer>
      
      <style jsx="true">{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -20px); }
          10% { opacity: 1; transform: translate(-50%, 0); }
          90% { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -20px); }
        }
        
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default RatingsManagement;